import { emptySplitApi as baseApi } from "./baseApi";

const api = baseApi.enhanceEndpoints({
  addTagTypes: [
    "ComsBlock",
    "ComsCatalog",
    "ComsReport",
    "ComsMatDelivery",
    "ComsWeeklyReport",
    "ComsMonthlyReportRecord",
  ] as const,
});

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ComsBlock {
  id?: string;
  code: string;
  name: string;
  description?: string;
  development?: string;
  createdAt?: string;
  /** 'type1' | 'type2' — links block to a shared system catalogue */
  projectType?: "type1" | "type2";
  // ── Opening Balances (Part B) ─────────────────────────────────
  openingActualCost?: number;
  openingActualCostMaterials?: number;
  openingActualCostLabour?: number;
  openingActualCostEquipment?: number;
  openingActualCostDate?: string;
  openingProfitLoss?: number;
  profitStartingBaseline?: number;
  floatingLabourAllocation?: number;
  floatingLabourNote?: string;
  floatingLabourEntries?: {
    month?: string;
    amount?: number;
    note?: string;
  }[];
  pettyCashAllocation?: number;
  pettyCashNote?: string;
  pettyCashEntries?: {
    month?: string;
    amount?: number;
    note?: string;
  }[];
}

export interface ComsEvmOverview {
  bac: number;
  ev: number;
  ac: number;
  cv: number;
  cpi: number;
  eac: number;
  vac: number;
  tcpi: number;
  openingActualCost: number;
  openingActualCostMaterials: number;
  openingActualCostLabour: number;
  openingActualCostEquipment: number;
  openingActualCostDate: string | null;
  openingProfitLoss: number;
  profitStartingBaseline: number;
  materialsCostToDate: number;
  labourCostToDate: number;
  equipmentCostToDate: number;
  floatingLabourAllocation: number;
  floatingLabourNote: string | null;
  floatingLabourEntries: {
    month?: string;
    amount?: number;
    note?: string;
  }[];
  pettyCashAllocation: number;
  pettyCashNote: string | null;
  pettyCashEntries: {
    month?: string;
    amount?: number;
    note?: string;
  }[];
}

export interface ComsBlockStats {
  block: ComsBlock;
  reportsCount: number;
  activitiesCount: number;
  materialsCount: number;
  labourCount: number;
  equipmentCount: number;
  catalogTotal: number;
}

export interface ComsCatalogItem {
  id?: string;
  /** Null/absent for shared system catalogue items; set for per-block items */
  blockId?: string;
  /** 'type1' | 'type2' — set on shared system catalogue items */
  catalogueType?: "type1" | "type2";
  /** Item code from the catalogue XLSX (e.g. 'A', 'B', '—') */
  itemCode?: string;
  /** Per-block activity item → ID of the shared catalogue item it mirrors */
  sharedItemId?: string;
  /** 'activity' | 'material' | 'labour' | 'equipment' */
  type: string;
  name: string;
  unit: string;
  defaultRate: number;
  /** Bill number (1–12), activities only */
  billNumber?: number;
  /** Planned quantity from BOQ / materials CSV import */
  plannedQty?: number;
  /** Whether this item has been marked as 100% complete */
  markComplete?: boolean;
  /** Pre-app-go-live quantity (activities: actual qty; materials: used qty from XLSX) */
  openingActualQty?: number;
  /** Adjustment stored at tick time: plannedQty − openingActualQty − dailyActualQty */
  markCompleteAdjQty?: number;
  /** Position within section to preserve CSV import order (global for materials) */
  displayOrder?: number;
  /** Section name (sheet name from catalogue XLSX) */
  section?: string;
}

export interface ComsActivityLine {
  lineId: string;
  catalogItemId?: string;
  name: string;
  unit: string;
  quantity: number;
  rate: number;
  amount: number;
}

export interface ComsMaterialLine {
  lineId: string;
  catalogItemId?: string;
  name: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

export interface ComsLabourLine {
  lineId: string;
  catalogItemId?: string;
  grade: string;
  unit: string;
  people: number;
  dayRate: number;
  amount: number;
}

export interface ComsEquipmentLine {
  lineId: string;
  catalogItemId?: string;
  name: string;
  unit: string;
  hours: number;
  hourlyRate: number;
  amount: number;
}

export interface ComsDailyReport {
  id?: string;
  blockId: string;
  reportDate: string; // YYYY-MM-DD
  notes?: string;
  activities?: ComsActivityLine[];
  materials?: ComsMaterialLine[];
  labour?: ComsLabourLine[];
  equipment?: ComsEquipmentLine[];
}

export interface ComsWorkDoneItem {
  catalogItemId?: string | null;
  name: string;
  unit: string;
  totalQuantity: number;
  totalAmount: number;
}

export interface ComsMatDelivery {
  id?: string;
  blockId: string;
  /** FK → ComsCatalogItem.id (type = 'material') */
  budgetItemId: string;
  /** YYYY-MM-DD */
  deliveryDate: string;
  orderNo: string;
  qtyReceived: number;
  isOpeningBalance?: boolean;
}

export interface ComsMonthlyReport {
  blockId: string;
  from: string;
  to: string;
  reportsCount: number;
  totalRevenue: number;
  totalMaterials: number;
  totalLabour: number;
  totalEquipment: number;
  totalProfit: number;
  reports: ComsDailyReport[];
}

export interface ReviewStep {
  role: "Site QS" | "Site Manager" | "PM" | "CPM";
  comment: string;
  decision: "pending" | "approved";
  submittedAt: string | null;
  userName: string | null;
}

export type ReviewRole = "Site QS" | "Site Manager";

export type WeeklyReportStatus =
  | "awaiting-site-qs"
  | "awaiting-site-manager"
  | "awaiting-pm"
  | "awaiting-cpm"
  | "approved";

export interface WorkDoneAgg {
  catalogItemId: string | null;
  name: string;
  unit: string;
  totalQuantity: number;
  totalAmount: number;
  plannedQty?: number;
  cumulativeQty?: number;
  plannedAmount?: number;
  cumulativeAmount?: number;
  periodPct?: number;
  cumulativePct?: number;
}

export interface MaterialAgg {
  catalogItemId: string | null;
  name: string;
  unit: string;
  totalQuantity: number;
  totalAmount: number;
}

export interface DailyGroup {
  date: string;
  lines: object[];
}

export interface ComsWeeklyReport {
  id?: string;
  blockId: string;
  weekStart: string;
  weekEnd: string;
  status?: WeeklyReportStatus;
  // Period snapshot
  workDone?: WorkDoneAgg[];
  materialsUsed?: MaterialAgg[];
  dailyLabour?: DailyGroup[];
  dailyEquipment?: DailyGroup[];
  // Period totals
  periodWorkDoneTotal?: number;
  periodMaterials?: number;
  periodLabour?: number;
  periodEquipment?: number;
  periodRevenue?: number;
  periodProfitLoss?: number;
  cumulativeRevenue?: number;
  cumulativeMaterials?: number;
  cumulativeLabour?: number;
  cumulativeEquipment?: number;
  cumulativeProfitLoss?: number;
  // EVM cumulative
  bac?: number;
  ev?: number;
  ac?: number;
  cv?: number;
  cpi?: number;
  eac?: number;
  vac?: number;
  tcpi?: number;
  evmNarrative?: string;
  // Review trail
  reviewTrail?: ReviewStep[];
  createdAt?: string;
}

export interface ComsMonthlyReportRecord {
  id?: string;
  blockId: string;
  monthStart: string;
  monthEnd: string;
  monthLabel?: string;
  status?: WeeklyReportStatus;
  reportsCount?: number;
  // Period snapshot
  workDone?: WorkDoneAgg[];
  materialsUsed?: MaterialAgg[];
  dailyLabour?: DailyGroup[];
  dailyEquipment?: DailyGroup[];
  // Period totals
  periodWorkDoneTotal?: number;
  periodMaterials?: number;
  periodLabour?: number;
  periodEquipment?: number;
  periodRevenue?: number;
  periodProfitLoss?: number;
  cumulativeRevenue?: number;
  cumulativeMaterials?: number;
  cumulativeLabour?: number;
  cumulativeEquipment?: number;
  cumulativeProfitLoss?: number;
  // EVM cumulative
  bac?: number;
  ev?: number;
  ac?: number;
  cv?: number;
  cpi?: number;
  eac?: number;
  vac?: number;
  tcpi?: number;
  evmNarrative?: string;
  // Review trail
  reviewTrail?: ReviewStep[];
  createdAt?: string;
}

// ─── RTK Query endpoints ──────────────────────────────────────────────────────

const comsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // Blocks
    getComsBlocks: build.query<ComsBlock[], void>({
      query: () => "/coms/blocks",
      providesTags: ["ComsBlock"],
    }),
    getComsBlockById: build.query<ComsBlock, string>({
      query: (id) => `/coms/blocks/${id}`,
      providesTags: (_r, _e, id) => [{ type: "ComsBlock", id }],
    }),
    getComsBlockStats: build.query<ComsBlockStats, string>({
      query: (id) => `/coms/blocks/${id}/stats`,
      providesTags: (_r, _e, id) => [{ type: "ComsBlock", id }],
    }),
    createComsBlock: build.mutation<
      ComsBlock,
      Omit<ComsBlock, "id" | "createdAt">
    >({
      query: (body) => ({ url: "/coms/blocks", method: "POST", body }),
      invalidatesTags: ["ComsBlock"],
    }),
    updateComsBlock: build.mutation<
      void,
      { id: string; data: Partial<ComsBlock> }
    >({
      query: ({ id, data }) => ({
        url: `/coms/blocks/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ComsBlock", id },
        "ComsBlock",
      ],
    }),
    deleteComsBlock: build.mutation<void, string>({
      query: (id) => ({ url: `/coms/blocks/${id}`, method: "DELETE" }),
      invalidatesTags: ["ComsBlock"],
    }),

    // Catalog
    getComsCatalog: build.query<
      ComsCatalogItem[],
      { blockId?: string; type?: string; catalogueType?: string }
    >({
      query: ({ blockId, type, catalogueType }) => {
        const params = new URLSearchParams();
        if (blockId) params.set("blockId", blockId);
        if (type) params.set("type", type);
        if (catalogueType) params.set("catalogueType", catalogueType);
        const qs = params.toString();
        return qs ? `/coms/catalog?${qs}` : "/coms/catalog";
      },
      providesTags: ["ComsCatalog"],
    }),
    createComsCatalogItem: build.mutation<
      ComsCatalogItem,
      Omit<ComsCatalogItem, "id">
    >({
      query: (body) => ({ url: "/coms/catalog", method: "POST", body }),
      invalidatesTags: ["ComsCatalog"],
    }),
    updateComsCatalogItem: build.mutation<
      void,
      { id: string; data: Partial<ComsCatalogItem> }
    >({
      query: ({ id, data }) => ({
        url: `/coms/catalog/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["ComsCatalog"],
    }),
    deleteComsCatalogItem: build.mutation<void, string>({
      query: (id) => ({ url: `/coms/catalog/${id}`, method: "DELETE" }),
      invalidatesTags: ["ComsCatalog"],
    }),

    // Daily Reports
    getComsDailyReports: build.query<ComsDailyReport[], string>({
      query: (blockId) => `/coms/reports?blockId=${blockId}`,
      providesTags: ["ComsReport"],
    }),
    getComsDailyReportById: build.query<ComsDailyReport, string>({
      query: (id) => `/coms/reports/${id}`,
      providesTags: (_r, _e, id) => [{ type: "ComsReport", id }],
    }),
    createComsDailyReport: build.mutation<
      ComsDailyReport,
      Omit<ComsDailyReport, "id">
    >({
      query: (body) => ({ url: "/coms/reports", method: "POST", body }),
      invalidatesTags: ["ComsReport"],
    }),
    updateComsDailyReport: build.mutation<
      void,
      { id: string; data: Partial<ComsDailyReport> }
    >({
      query: ({ id, data }) => ({
        url: `/coms/reports/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ComsReport", id },
        "ComsReport",
      ],
    }),
    deleteComsDailyReport: build.mutation<void, string>({
      query: (id) => ({ url: `/coms/reports/${id}`, method: "DELETE" }),
      invalidatesTags: ["ComsReport"],
    }),
    getComsWorkDone: build.query<ComsWorkDoneItem[], string>({
      query: (blockId) => `/coms/reports/work-done?blockId=${blockId}`,
      providesTags: ["ComsReport"],
    }),
    getComsMonthlyReport: build.query<
      ComsMonthlyReport,
      { blockId: string; from: string; to: string }
    >({
      query: ({ blockId, from, to }) =>
        `/coms/reports/monthly?blockId=${blockId}&from=${from}&to=${to}`,
    }),

    // Materials deliveries
    getComsMatDeliveries: build.query<
      ComsMatDelivery[],
      { blockId?: string; budgetItemId?: string }
    >({
      query: ({ blockId, budgetItemId }) => {
        const params = new URLSearchParams();
        if (blockId) params.set("blockId", blockId);
        if (budgetItemId) params.set("budgetItemId", budgetItemId);
        const qs = params.toString();
        return qs ? `/coms/mat-deliveries?${qs}` : "/coms/mat-deliveries";
      },
      providesTags: ["ComsMatDelivery"],
    }),
    createComsMatDelivery: build.mutation<
      ComsMatDelivery,
      Omit<ComsMatDelivery, "id">
    >({
      query: (body) => ({ url: "/coms/mat-deliveries", method: "POST", body }),
      invalidatesTags: ["ComsMatDelivery"],
    }),
    deleteComsMatDelivery: build.mutation<void, string>({
      query: (id) => ({ url: `/coms/mat-deliveries/${id}`, method: "DELETE" }),
      invalidatesTags: ["ComsMatDelivery"],
    }),

    // Weekly Reports
    getComsWeeklyReports: build.query<ComsWeeklyReport[], string>({
      query: (blockId) => `/coms/weekly-reports?blockId=${blockId}`,
      providesTags: ["ComsWeeklyReport"],
    }),
    getComsWeeklyReportById: build.query<ComsWeeklyReport, string>({
      query: (id) => `/coms/weekly-reports/${id}`,
      providesTags: (_r, _e, id) => [{ type: "ComsWeeklyReport", id }],
    }),
    generateComsWeeklyReport: build.mutation<
      ComsWeeklyReport,
      { blockId: string; weekStart: string; weekEnd: string; force?: boolean }
    >({
      query: (body) => ({
        url: "/coms/weekly-reports/generate",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ComsWeeklyReport"],
    }),
    backfillComsWeeklyReports: build.mutation<
      { generated: number },
      { blockId: string }
    >({
      query: (body) => ({
        url: "/coms/weekly-reports/backfill",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ComsWeeklyReport"],
    }),
    regenerateComsWeeklyReportForDate: build.mutation<
      ComsWeeklyReport | null,
      { blockId: string; date: string }
    >({
      query: (body) => ({
        url: "/coms/weekly-reports/regenerate-for-date",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ComsWeeklyReport"],
    }),
    updateComsWeeklyReport: build.mutation<
      ComsWeeklyReport,
      { id: string; reviewTrail: ReviewStep[] }
    >({
      query: ({ id, reviewTrail }) => ({
        url: `/coms/weekly-reports/${id}`,
        method: "PATCH",
        body: { reviewTrail },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ComsWeeklyReport", id },
        "ComsWeeklyReport",
      ],
    }),

    // Monthly Report Records (auto-generated, stored instances)
    getComsMonthlyReportRecords: build.query<ComsMonthlyReportRecord[], string>(
      {
        query: (blockId) => `/coms/monthly-report-records?blockId=${blockId}`,
        providesTags: ["ComsMonthlyReportRecord"],
      },
    ),
    getComsMonthlyReportRecordById: build.query<
      ComsMonthlyReportRecord,
      string
    >({
      query: (id) => `/coms/monthly-report-records/${id}`,
      providesTags: (_r, _e, id) => [{ type: "ComsMonthlyReportRecord", id }],
    }),
    generateComsMonthlyReportRecord: build.mutation<
      ComsMonthlyReportRecord,
      { blockId: string; monthStart: string; monthEnd: string; force?: boolean }
    >({
      query: (body) => ({
        url: "/coms/monthly-report-records/generate",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ComsMonthlyReportRecord"],
    }),
    backfillComsMonthlyReportRecords: build.mutation<
      { generated: number },
      { blockId: string }
    >({
      query: (body) => ({
        url: "/coms/monthly-report-records/backfill",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ComsMonthlyReportRecord"],
    }),
    regenerateComsMonthlyReportRecordForDate: build.mutation<
      ComsMonthlyReportRecord | null,
      { blockId: string; date: string }
    >({
      query: (body) => ({
        url: "/coms/monthly-report-records/regenerate-for-date",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ComsMonthlyReportRecord"],
    }),
    updateComsMonthlyReportRecord: build.mutation<
      ComsMonthlyReportRecord,
      { id: string; reviewTrail: ReviewStep[] }
    >({
      query: ({ id, reviewTrail }) => ({
        url: `/coms/monthly-report-records/${id}`,
        method: "PATCH",
        body: { reviewTrail },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "ComsMonthlyReportRecord", id },
        "ComsMonthlyReportRecord",
      ],
    }),

    // Block EVM overview (live calculation for Overview tab)
    getComsBlockEvmOverview: build.query<ComsEvmOverview, string>({
      query: (blockId) => `/coms/blocks/${blockId}/evm-overview`,
      providesTags: (_r, _e, blockId) => [{ type: "ComsBlock", id: blockId }],
    }),

    // Block has-actuals check
    getComsBlockHasActuals: build.query<{ hasActuals: boolean }, string>({
      query: (blockId) => `/coms/blocks/${blockId}/has-actuals`,
      providesTags: (_r, _e, blockId) => [{ type: "ComsBlock", id: blockId }],
    }),

    // Upload Audit (per block)
    getComsUploadAudit: build.query<
      {
        id?: string;
        blockId: string;
        action: string;
        performedAt: string;
        diff?: object;
      }[],
      string
    >({
      query: (blockId) => `/coms/blocks/${blockId}/upload-audit`,
      providesTags: (_r, _e, blockId) => [{ type: "ComsBlock", id: blockId }],
    }),
    createComsUploadAudit: build.mutation<
      object,
      { blockId: string; action?: string; diff?: object }
    >({
      query: ({ blockId, action, diff }) => ({
        url: `/coms/blocks/${blockId}/upload-audit`,
        method: "POST",
        body: { action, diff },
      }),
      invalidatesTags: (_r, _e, { blockId }) => [
        { type: "ComsBlock", id: blockId },
      ],
    }),

    // Baseline actuals upload (sets openingActualQty + plannedQty per shared catalogue item)
    uploadBaselineActuals: build.mutation<
      { matched: number; unmatched: object[]; overwritten: number },
      {
        blockId: string;
        overwrite: boolean;
        items: {
          sharedItemId?: string;
          itemCode: string;
          billNumber: number;
          description: string;
          unit: string;
          plannedQty: number;
          openingActualQty: number;
        }[];
      }
    >({
      query: ({ blockId, overwrite, items }) => ({
        url: `/coms/blocks/${blockId}/baseline-actuals`,
        method: "POST",
        body: { overwrite, items },
      }),
      invalidatesTags: ["ComsCatalog"],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetComsBlocksQuery,
  useGetComsBlockByIdQuery,
  useGetComsBlockStatsQuery,
  useCreateComsBlockMutation,
  useUpdateComsBlockMutation,
  useDeleteComsBlockMutation,
  useGetComsCatalogQuery,
  useCreateComsCatalogItemMutation,
  useUpdateComsCatalogItemMutation,
  useDeleteComsCatalogItemMutation,
  useGetComsDailyReportsQuery,
  useGetComsDailyReportByIdQuery,
  useCreateComsDailyReportMutation,
  useUpdateComsDailyReportMutation,
  useDeleteComsDailyReportMutation,
  useGetComsWorkDoneQuery,
  useGetComsMonthlyReportQuery,
  useGetComsMatDeliveriesQuery,
  useCreateComsMatDeliveryMutation,
  useDeleteComsMatDeliveryMutation,
  useGetComsWeeklyReportsQuery,
  useGetComsWeeklyReportByIdQuery,
  useGenerateComsWeeklyReportMutation,
  useUpdateComsWeeklyReportMutation,
  useBackfillComsWeeklyReportsMutation,
  useGetComsMonthlyReportRecordsQuery,
  useGetComsMonthlyReportRecordByIdQuery,
  useGenerateComsMonthlyReportRecordMutation,
  useUpdateComsMonthlyReportRecordMutation,
  useBackfillComsMonthlyReportRecordsMutation,
  useGetComsUploadAuditQuery,
  useCreateComsUploadAuditMutation,
  useGetComsBlockHasActualsQuery,
  useUploadBaselineActualsMutation,
  useGetComsBlockEvmOverviewQuery,
  useRegenerateComsWeeklyReportForDateMutation,
  useRegenerateComsMonthlyReportRecordForDateMutation,
} = comsApi;
